import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type { RemsProjectConfigurationDto, RemsProjectConfigurationRequest } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { Input } from "@/design-system/Input";
import { Notice } from "@/design-system/Notice";
import { SectionCard } from "@/design-system/SectionCard";
import { Select } from "@/design-system/Select";
import { Tiles } from "@/design-system/Tiles";
import { QUERY_KEYS } from "@/query-keys";
import type { TranslationKey } from "@/translations";
import { useTranslation } from "@/translations";

import { BulkOperationResults, BulkOperationReview, type BulkOperationReviewField } from "./BulkOperationReview";
import type { RemsBulkEditableField } from "./configurationDefinitions";
import type { PreparedBulkProject, RemsOperation } from "./helpers";
import {
  commonValue,
  CONFIGURATION_REQUEST_CONCURRENCY,
  getErrorMessage,
  mapWithConcurrency,
  prepareRemsProject,
} from "./helpers";
import type { BulkOperationResult, PortfolioIntegrationProject } from "./types";

const REMS_OPERATIONS = ["configure", "enableTickets", "enableContracts", "disable"] as const;

const REMS_OPERATION_LABELS = {
  configure: "page.portfolio-integrations.operation.configuration",
  enableTickets: "page.portfolio-integrations.rems.operation.enable-tickets",
  enableContracts: "page.portfolio-integrations.rems.operation.enable-contracts",
  disable: "page.portfolio-integrations.rems.operation.disable",
} as const satisfies Record<RemsOperation, TranslationKey>;

type BulkRemsEditorProps = {
  rows: PortfolioIntegrationProject<RemsProjectConfigurationDto>[];
  selectedProjectIds: Set<string>;
  onContinueToProjectSetup: () => void;
};

export function BulkRemsEditor({
  rows,
  selectedProjectIds,
  onContinueToProjectSetup,
}: BulkRemsEditorProps): React.ReactNode {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const selectedRows = useMemo(
    () => rows.filter((row) => row.canManage && selectedProjectIds.has(row.project.id)),
    [rows, selectedProjectIds],
  );
  const rowsRef = useRef(rows);
  const selectionKey = JSON.stringify([...selectedProjectIds].sort());

  const [operation, setOperation] = useState<RemsOperation>("configure");
  const [stage, setStage] = useState<"edit" | "review" | "results">("edit");
  const [isApplying, setIsApplying] = useState(false);
  const [results, setResults] = useState<BulkOperationResult[]>([]);
  const [changedFields, setChangedFields] = useState<Record<RemsBulkEditableField, boolean>>({
    tenantId: false,
    communityUserContactId: false,
    communityUserRelationId: false,
    defectCode: false,
  });
  const [values, setValues] = useState<Record<RemsBulkEditableField, string>>({
    tenantId: "",
    communityUserContactId: "",
    communityUserRelationId: "",
    defectCode: "",
  });

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    const currentProjectIds = new Set<string>(JSON.parse(selectionKey));
    const currentRows = rowsRef.current.filter((row) => currentProjectIds.has(row.project.id));
    setValues({
      tenantId: commonValue(currentRows.map((row) => row.configuration.tenantId)),
      communityUserContactId: commonValue(currentRows.map((row) => row.configuration.communityUserContactId)),
      communityUserRelationId: commonValue(currentRows.map((row) => row.configuration.communityUserRelationId)),
      defectCode: commonValue(currentRows.map((row) => row.configuration.tickets.defectCode)),
    });
    setChangedFields({
      tenantId: false,
      communityUserContactId: false,
      communityUserRelationId: false,
      defectCode: false,
    });
    setStage("edit");
  }, [selectionKey]);

  const preparedProjects = selectedRows.map((row) =>
    prepareRemsProject(row, operation, changedFields, values, {
      identifiers: t("page.portfolio-integrations.preflight.rems-identifiers"),
      complexCode: t("page.portfolio-integrations.preflight.rems-complex-code"),
      defectCode: t("page.portfolio-integrations.preflight.rems-defect-code"),
      emptySharedValue: t("page.portfolio-integrations.preflight.rems-empty-shared-value"),
    }),
  );
  const validProjects = preparedProjects.filter((project) => !project.reason);
  const skippedProjects = preparedProjects.filter((project) => project.reason) as Array<
    PreparedBulkProject<RemsProjectConfigurationRequest> & { reason: string }
  >;

  const fields = useMemo(() => {
    const labels: Record<RemsBulkEditableField, string> = {
      tenantId: t("page.integration.rems.form.tenant-id"),
      communityUserContactId: t("page.integration.rems.form.community-user-contact-id"),
      communityUserRelationId: t("page.integration.rems.form.community-user-relation-id"),
      defectCode: t("page.integration.rems.form.defect-code"),
    };

    const fields: BulkOperationReviewField[] = (Object.keys(changedFields) as RemsBulkEditableField[])
      .filter((field) => changedFields[field])
      .map((field) => ({ label: labels[field], value: values[field].trim() }));
    if (operation === "enableTickets") {
      fields.push({
        label: t("page.portfolio-integrations.activation.title"),
        value: t("page.portfolio-integrations.rems.operation.enable-tickets"),
      });
    } else if (operation === "enableContracts") {
      fields.push({
        label: t("page.portfolio-integrations.activation.title"),
        value: t("page.portfolio-integrations.rems.operation.enable-contracts"),
      });
    } else if (operation === "disable") {
      fields.push({
        label: t("page.portfolio-integrations.activation.title"),
        value: t("page.portfolio-integrations.rems.operation.disable"),
      });
    }

    return fields;
  }, [changedFields, operation, t, values]);
  const hasBlankSharedValue = (Object.keys(changedFields) as RemsBulkEditableField[]).some(
    (field) => changedFields[field] && !values[field].trim(),
  );

  const apply = async (projectIds?: string[]) => {
    if (validProjects.length === 0) {
      return;
    }

    setIsApplying(true);
    try {
      const targets = validProjects.filter((project) => !projectIds || projectIds.includes(project.id));
      const settled = await mapWithConcurrency(targets, CONFIGURATION_REQUEST_CONCURRENCY, async (project) => {
        try {
          await api.putIntegrationsRemsV1(project.payload, {
            headers: { [ProjectHeaderName]: project.id },
          });

          return { status: "fulfilled", value: project } as const;
        } catch (reason) {
          return { status: "rejected", reason } as const;
        }
      });

      const updatedResults = settled.map((result, index): BulkOperationResult => {
        const project = targets[index];

        return result.status === "fulfilled"
          ? { projectId: project.id, projectName: project.name, status: "updated" }
          : {
              projectId: project.id,
              projectName: project.name,
              status: "failed",
              reason: getErrorMessage(result.reason, t("page.portfolio-integrations.results.update-failed")),
            };
      });
      await Promise.all([
        ...updatedResults
          .filter((result) => result.status === "updated")
          .map((result) =>
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.INTEGRATION_REMS_CONFIGURATION(result.projectId) }),
          ),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_INTEGRATION_CONFIGURATIONS("rems") }),
      ]);

      if (projectIds) {
        setResults((current) => [
          ...current.filter((result) => !projectIds.includes(result.projectId)),
          ...updatedResults,
        ]);
      } else {
        setResults([
          ...updatedResults,
          ...skippedProjects.map((project) => ({
            projectId: project.id,
            projectName: project.name,
            status: "skipped" as const,
            reason: project.reason,
          })),
        ]);
      }
      setStage("results");
    } finally {
      setIsApplying(false);
    }
  };

  if (stage === "results") {
    return (
      <BulkOperationResults
        results={results}
        isRetrying={isApplying}
        onRetry={(projectIds) => void apply(projectIds)}
        onContinue={onContinueToProjectSetup}
      />
    );
  }

  return (
    <>
      <SectionCard
        title={t("page.portfolio-integrations.rems.bulk-editable-title")}
        description={t("page.portfolio-integrations.rems.bulk-editable-description")}
      >
        <Tiles columns={{ base: 1, container: { md: 2 } }} gap="md">
          <BulkEditableRemsField
            field="tenantId"
            label={t("page.integration.rems.form.tenant-id")}
            value={values.tenantId}
            changed={changedFields.tenantId}
            placeholder="00000000-0000-0000-0000-000000000000"
            onChanged={(changed) => setChangedFields((current) => ({ ...current, tenantId: changed }))}
            onValue={(value) => setValues((current) => ({ ...current, tenantId: value }))}
          />
          <BulkEditableRemsField
            field="communityUserContactId"
            label={t("page.integration.rems.form.community-user-contact-id")}
            value={values.communityUserContactId}
            changed={changedFields.communityUserContactId}
            placeholder="1234"
            onChanged={(changed) => setChangedFields((current) => ({ ...current, communityUserContactId: changed }))}
            onValue={(value) => setValues((current) => ({ ...current, communityUserContactId: value }))}
          />
          <BulkEditableRemsField
            field="communityUserRelationId"
            label={t("page.integration.rems.form.community-user-relation-id")}
            value={values.communityUserRelationId}
            changed={changedFields.communityUserRelationId}
            placeholder="1234"
            onChanged={(changed) => setChangedFields((current) => ({ ...current, communityUserRelationId: changed }))}
            onValue={(value) => setValues((current) => ({ ...current, communityUserRelationId: value }))}
          />
          <BulkEditableRemsField
            field="defectCode"
            label={t("page.integration.rems.form.defect-code")}
            value={values.defectCode}
            changed={changedFields.defectCode}
            placeholder="AA00000"
            onChanged={(changed) => setChangedFields((current) => ({ ...current, defectCode: changed }))}
            onValue={(value) => setValues((current) => ({ ...current, defectCode: value }))}
          />
        </Tiles>

        {hasBlankSharedValue && (
          <Notice variant="warning" message={t("page.portfolio-integrations.preflight.rems-empty-shared-value")} />
        )}

        <FormField
          label={t("page.portfolio-integrations.activation.title")}
          description={t("page.portfolio-integrations.activation.description")}
        >
          <Select
            width="standard"
            items={REMS_OPERATIONS}
            selected={operation}
            keySelector={(item) => item}
            renderOption={(item) => t(REMS_OPERATION_LABELS[item])}
            onChange={setOperation}
          />
        </FormField>

        <Form.Footer
          renderPrimaryAction={() => (
            <Button
              disabled={
                selectedRows.length === 0 || hasBlankSharedValue || (operation === "configure" && fields.length === 0)
              }
              onClick={() => setStage("review")}
            >
              {t("page.portfolio-integrations.action.review")}
            </Button>
          )}
        />
      </SectionCard>
      {stage === "review" && (
        <BulkOperationReview
          title={t("page.portfolio-integrations.review.title")}
          description={t(
            operation === "configure"
              ? "page.portfolio-integrations.review.activation-preserved"
              : "page.portfolio-integrations.review.activation-explicit",
          )}
          fields={fields}
          validProjects={validProjects}
          skippedProjects={skippedProjects}
          isApplying={isApplying}
          onBack={() => setStage("edit")}
          onApply={() => void apply()}
        />
      )}
    </>
  );
}

function BulkEditableRemsField({
  field,
  label,
  value,
  changed,
  placeholder,
  onChanged,
  onValue,
}: {
  field: RemsBulkEditableField;
  label: string;
  value: string;
  changed: boolean;
  placeholder: string;
  onChanged: (changed: boolean) => void;
  onValue: (value: string) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <FormField
      label={label}
      description={
        changed
          ? t("page.portfolio-integrations.field.will-change")
          : t("page.portfolio-integrations.field.keep-current")
      }
      actions={
        <Checkbox
          name={`change-${field}`}
          checked={changed}
          label={t("page.portfolio-integrations.field.change")}
          onChange={(event) => onChanged(event.target.checked)}
        />
      }
    >
      <Input
        value={value}
        disabled={!changed}
        placeholder={value ? placeholder : t("page.portfolio-integrations.field.multiple-values")}
        onChange={(event) => onValue(event.target.value)}
      />
    </FormField>
  );
}
